// src/contexts/AudioPlayerContext.js - UPDATED WITH METADATA PROXY (CORS-FIX)
import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';

const normalizeStations = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.stations)
      ? data.stations
      : [];

  return list
    .filter(s => s && typeof s === 'object')
    .map((s, idx) => ({
      ...s,
      id: s.id || `station-${idx}`,
    }))
    .filter(s => Boolean(s.name) && Boolean(s.streamUrl));
};

const AudioPlayerContext = createContext();

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

const FALLBACK_STATIONS = [
  
];

// Build a request URL for metadata, optionally via a proxy Worker
const buildMetadataRequestUrl = (metadataUrl) => {
  if (!metadataUrl) return null;

  const baseRaw = process.env.REACT_APP_METADATA_PROXY_BASE || '';
  const base = baseRaw.replace(/\/+$/, ''); // trim trailing /

  // If no proxy base set, fall back to direct (may CORS-fail for some stations)
  if (!base) return metadataUrl;

  // Proxy endpoint: `${base}/api/metadata?url=<encoded upstream>`
  const cacheBust = `t=${Date.now()}`;
  const glue = base.includes('?') ? '&' : '?'; // not really needed but safe
  // Add cache-bust at the *proxy* layer (Worker still fetches upstream fresh)
  return `${base}/api/metadata?url=${encodeURIComponent(metadataUrl)}&${cacheBust}${glue}`;
};

export const AudioPlayerProvider = ({ children }) => {
  // Station data
  const [stations, setStations] = useState(FALLBACK_STATIONS);

  const [currentStation, setCurrentStationState] = useState(FALLBACK_STATIONS[0] || null);
  
  // Updated track info state to match new API structure
  const [trackInfo, setTrackInfo] = useState({
    title: FALLBACK_STATIONS[0]?.name || '',
    start_time: null,
    artwork_urls: {
      standard: FALLBACK_STATIONS[0]?.showArt || '',
      large: FALLBACK_STATIONS[0]?.showArt || ''
    },
    track_artist: 'Utter Radio',
    track_title: `${'Utter Radio' || 'Unknown'}`,
    track_album: 'Unknown',
    track_playout_duration: 0,
    track_duration: 0
  });
  
  const [showArt, setShowArt] = useState(FALLBACK_STATIONS[0]?.showArt || '');
  const [showTitle, setShowTitle] = useState(FALLBACK_STATIONS[0]?.name || '');
  const [catchupShowArt, setCatchupShowArt] = useState('');

  // Metadata state
  const [metadata, setMetadata] = useState({});
  const [recentTracks, setRecentTracks] = useState({});
  const metadataIntervalsRef = useRef({});

  // Player state
  const [isPlaying, setIsPlayingState] = useState(false);
  const [volume, setVolumeState] = useState(0.8);

  // Catchup mode state
  const [isCatchupMode, setIsCatchupMode] = useState(false);
  const [catchupCurrentTime, setCatchupCurrentTime] = useState(0);
  const [catchupDuration, setCatchupDuration] = useState(0);

  // Audio references
  const liveAudioRef = useRef(new Audio());
  const catchupAudioRef = useRef(new Audio());

  // --- METADATA FUNCTIONS ---
  const fetchMetadataForStation = useCallback(async (station) => {
    if (!station?.metadataUrl) return null;

    try {
      // For demo stations, use mock data in new API format
      if (station.metadataUrl.includes('example.com')) {
        const mockData = {
          data: {
            title: station.name,
            start_time: new Date().toISOString(),
            artwork_urls: {
              standard: station.showArt || '',
              large: station.showArt || ''
            },
            track_artist: 'Various Artists',
            track_title: `${station.genre} Mix`,
            track_album: `${station.genre} Collection`,
            track_playout_duration: 0,
            track_duration: 0
          }
        };

        return mockData;
      }

      // Real API call (via Worker proxy if REACT_APP_METADATA_PROXY_BASE is set)
      const requestUrl = buildMetadataRequestUrl(station.metadataUrl) || station.metadataUrl;

      const response = await fetch(requestUrl, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle error responses
      if (data && data.success === false) {
        console.warn('Metadata upstream returned error JSON:', station.id, data);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch metadata for station:', station?.id, error);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadStations() {
      try {
        const base = process.env.PUBLIC_URL || '';
        const url = `${base}/stations.json?t=${Date.now()}`; // cache-bust
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load stations.json (${res.status})`);

        const data = await res.json();
        const list = normalizeStations(data);

        if (!cancelled) {
          setStations(list.length ? list : FALLBACK_STATIONS);
        }
      } catch (e) {
        console.error('Stations load error:', e);
        if (!cancelled) setStations(FALLBACK_STATIONS);
      }
    }

    loadStations();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!stations.length) return;

    const stillExists =
      currentStation && stations.some(s => s.id === currentStation.id);

    if (!currentStation || !stillExists) {
      const next = stations[0];
      setCurrentStationState(next);

      // Set baseline UI state in new API format (metadata polling will refine it)
      setTrackInfo({
        title: next?.name || '',
        start_time: null,
        artwork_urls: {
          standard: next?.showArt || '',
          large: next?.showArt || ''
        },
        track_artist: 'XMDV Radio',
        track_title: `Genre: ${next?.genre || 'Unknown'}`,
        track_album: 'Unknown',
        track_playout_duration: 0,
        track_duration: 0
      });
      setShowArt(next?.showArt || '');
      setShowTitle(next?.name || '');

      // Prime the audio element source
      const audio = liveAudioRef.current;
      if (next?.streamUrl && audio.src !== next.streamUrl) {
        audio.src = next.streamUrl;
        audio.load();
      }
    }
  }, [stations, currentStation]);

  // Update track info from metadata (adapted for new API structure)
  const updateTrackInfoFromMetadata = useCallback((station, metadataObj) => {
    if (!station || !metadataObj) return;

    // Check if the new API structure exists
    if (metadataObj.data) {
      // New API structure
      const data = metadataObj.data;
      
      setTrackInfo({
        title: data.title || station.name,
        start_time: data.start_time || null,
        artwork_urls: {
          standard: data.artwork_urls?.standard || station.showArt || '',
          large: data.artwork_urls?.large || station.showArt || ''
        },
        track_artist: data.track_artist || 'XMDV Radio',
        track_title: data.track_title || `Genre: ${station.genre}`,
        track_album: data.track_album || 'Unknown',
        track_playout_duration: data.track_playout_duration || 0,
        track_duration: data.track_duration || 0
      });
      
      setShowArt(data.artwork_urls?.large || data.artwork_urls?.standard || station.showArt || '');
      setShowTitle(data.title || station.name || '');
    } else {
      // Legacy API structure (backward compatibility)
      const song = metadataObj.now_playing?.song;
      const show = metadataObj.live?.streamer_name || 'JukeBox';
      
      setTrackInfo({
        title: show || station.name,
        start_time: null,
        artwork_urls: {
          standard: song?.art || station.showArt || '',
          large: song?.art || station.showArt || ''
        },
        track_artist: song?.artist || 'Various Artists',
        track_title: song ? song.title : `Genre: ${station.genre}`,
        track_album: 'Unknown',
        track_playout_duration: 0,
        track_duration: 0
      });
      
      setShowArt(song?.art || station.showArt || '');
      setShowTitle(show || station.name || '');
    }
  }, []);

  // Process and store metadata
  const processMetadata = useCallback((station, data) => {
    if (!station || !data) return;

    // Store metadata
    setMetadata(prev => ({
      ...prev,
      [station.id]: data
    }));

    // Store recent tracks (handle both old and new API structures)
    if (data?.data) {
      // New API structure - no song_history in the example, but we'll keep this flexible
      const history = data.song_history || [];
      if (Array.isArray(history)) {
        setRecentTracks(prev => ({
          ...prev,
          [station.id]: history.slice(0, 4)
        }));
      }
    } else if (data?.song_history && Array.isArray(data.song_history)) {
      // Legacy API structure
      setRecentTracks(prev => ({
        ...prev,
        [station.id]: data.song_history.slice(0, 4)
      }));
    }

    // If this is the current station, update track info
    if (currentStation?.id === station.id && !isCatchupMode) {
      updateTrackInfoFromMetadata(station, data);
    }
  }, [currentStation, isCatchupMode, updateTrackInfoFromMetadata]);

  // Load metadata for a station
  const loadMetadataForStation = useCallback(async (station) => {
    const data = await fetchMetadataForStation(station);
    if (data) {
      processMetadata(station, data);
    }
    return data;
  }, [fetchMetadataForStation, processMetadata]);

  // Start metadata polling for a station
  const startMetadataPolling = useCallback((station) => {
    if (!station) return;

    // Clear any existing interval for this station
    if (metadataIntervalsRef.current[station.id]) {
      clearInterval(metadataIntervalsRef.current[station.id]);
    }

    // Load metadata immediately
    loadMetadataForStation(station);

    // Set up interval to refresh metadata
    const intervalMs =
      Number.isFinite(station.metadataPollIntervalMs)
        ? station.metadataPollIntervalMs
        : (station.id === 'utter-radio' ? 2000 : 15000);

    if (intervalMs > 0 && station.metadataUrl) {
      metadataIntervalsRef.current[station.id] = setInterval(() => {
        loadMetadataForStation(station);
      }, intervalMs);
    }
  }, [loadMetadataForStation]);

  // Stop all metadata polling
  const stopAllMetadataPolling = useCallback(() => {
    Object.values(metadataIntervalsRef.current).forEach(clearInterval);
    metadataIntervalsRef.current = {};
  }, []);

  // --- AUDIO ELEMENT SETUP ---
  useEffect(() => {
    const audio = liveAudioRef.current;
    audio.volume = volume;
    audio.preload = 'none';

    const handlePlay = () => {
      setIsPlayingState(true);
      // When live audio plays, exit catchup mode
      if (isCatchupMode) {
        setIsCatchupMode(false);
        catchupAudioRef.current.pause();
      }
    };

    const handlePause = () => setIsPlayingState(false);
    const handleEnded = () => setIsPlayingState(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isCatchupMode, volume]);

  // Setup catchup audio event listeners
  useEffect(() => {
    const audio = catchupAudioRef.current;
    audio.volume = volume;
    audio.preload = 'none';

    const handlePlay = () => {
      setIsPlayingState(true);
      setIsCatchupMode(true);
      // When catchup plays, stop live audio
      if (!liveAudioRef.current.paused) {
        liveAudioRef.current.pause();
        stopAllMetadataPolling();
      }
    };

    const handlePause = () => setIsPlayingState(false);
    const handleTimeUpdate = () => {
      setCatchupCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setCatchupDuration(audio.duration);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [volume, stopAllMetadataPolling]);

  // Start metadata polling for current station
  useEffect(() => {
    if (currentStation && !isCatchupMode) {
      startMetadataPolling(currentStation);
    }

    return () => {
      stopAllMetadataPolling();
    };
  }, [currentStation, isCatchupMode, startMetadataPolling, stopAllMetadataPolling]);

  // Update volume
  useEffect(() => {
    liveAudioRef.current.volume = volume;
    catchupAudioRef.current.volume = volume;
  }, [volume]);

  // --- STATION CONTROL ---
  const setCurrentStation = useCallback((station, autoplay = false) => {
    if (!station || !station.streamUrl) return;

    // If switching from catchup, stop catchup
    if (isCatchupMode) {
      catchupAudioRef.current.pause();
      setIsCatchupMode(false);
    }

    setCurrentStationState(station);

    // Update track info in new API format
    setTrackInfo({
      title: station.name || '',
      start_time: null,
      artwork_urls: {
        standard: station.showArt || '',
        large: station.showArt || ''
      },
      track_artist: 'XMDV Radio',
      track_title: `Genre: ${station.genre || 'Unknown'}`,
      track_album: 'Unknown',
      track_playout_duration: 0,
      track_duration: 0
    });
    setShowArt(station.showArt || '');
    setShowTitle(station.name || '');

    // Set and play live audio
    const audio = liveAudioRef.current;
    if (audio.src !== station.streamUrl) {
      audio.src = station.streamUrl;
      audio.load();
    }

    if (autoplay) {
      audio.play().catch(console.error);
    }

    // Start metadata polling for this station
    startMetadataPolling(station);
  }, [isCatchupMode, startMetadataPolling]);

  // --- PLAYBACK CONTROLS ---
  const togglePlayback = useCallback(() => {
    if (isCatchupMode) {
      const audio = catchupAudioRef.current;
      if (audio.paused) {
        audio.play().catch(console.error);
      } else {
        audio.pause();
      }
    } else {
      if (!currentStation) return;

      const audio = liveAudioRef.current;

      if (audio.paused) {
        // FORCE LIVE: reset stream every time
        audio.pause();
        audio.src = `${currentStation.streamUrl}?t=${Date.now()}`;
        audio.load();
        audio.play().catch(console.error);
      } else {
        audio.pause();
      }
    }
  }, [isCatchupMode, currentStation]);

  const playCatchup = useCallback(() => {
    setIsCatchupMode(true);

    if (!liveAudioRef.current.paused) {
      liveAudioRef.current.pause();
      stopAllMetadataPolling();
    }

    const audio = catchupAudioRef.current;
    if (audio.src) {
      audio.play().catch(console.error);
    }
  }, [stopAllMetadataPolling]);

  const switchToLiveRadio = useCallback((station) => {
    if (!station || !station.streamUrl) {
      if (currentStation && currentStation.streamUrl) {
        setCurrentStation(currentStation, true);
      }
      return;
    }

    if (isCatchupMode) {
      catchupAudioRef.current.pause();
      setIsCatchupMode(false);
    }

    setCurrentStation(station, true);
  }, [isCatchupMode, currentStation, setCurrentStation]);

  const loadCatchupEpisode = useCallback((episodeUrl, episodeTitle, showTitleArg, showArtArg, onEndedCallback) => {
    if (!liveAudioRef.current.paused) {
      liveAudioRef.current.pause();
      stopAllMetadataPolling();
    }

    const audio = catchupAudioRef.current;
    audio.src = episodeUrl;
    audio.load();

    if (typeof onEndedCallback === 'function') {
      audio.onended = () => {
        setIsPlayingState(false);
        onEndedCallback();
      };
    }

    setIsCatchupMode(true);

    // Set the simple catchup art state
    setCatchupShowArt(showArtArg || '');
    setShowTitle(showTitleArg || '');
    setShowArt(showArtArg || '');
    
    setTrackInfo({
      title: showTitleArg || 'Catchup Show',
      artwork_urls: {
        standard: showArtArg || '',
        large: showArtArg || ''
      },
      track_artist: showTitleArg || 'Catchup',
      track_title: episodeTitle || 'Catchup Episode'
    });
}, [stopAllMetadataPolling]);

  // Manually update track info - useful for components that need to set specific track data
  const updateTrackInfo = useCallback((newTrackInfo) => {
    if (!newTrackInfo) return;
    
    setTrackInfo(prev => ({
      ...prev,
      ...newTrackInfo,
      // Ensure artwork_urls is properly merged
      artwork_urls: newTrackInfo.artwork_urls ? {
        ...prev.artwork_urls,
        ...newTrackInfo.artwork_urls
      } : prev.artwork_urls
    }));
    
    // Also update showArt and showTitle if provided
    if (newTrackInfo.title) {
      setShowTitle(newTrackInfo.title);
    }
    if (newTrackInfo.artwork_urls?.large) {
      setShowArt(newTrackInfo.artwork_urls.large);
    } else if (newTrackInfo.artwork_urls?.standard) {
      setShowArt(newTrackInfo.artwork_urls.standard);
    }
  }, []);

  const setVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
  }, []);

  const skipForward = useCallback((seconds = 10) => {
    if (isCatchupMode && catchupAudioRef.current) {
      catchupAudioRef.current.currentTime = Math.min(
        catchupAudioRef.current.currentTime + seconds,
        catchupAudioRef.current.duration
      );
    }
  }, [isCatchupMode]);

  const skipBackward = useCallback((seconds = 10) => {
    if (isCatchupMode && catchupAudioRef.current) {
      catchupAudioRef.current.currentTime = Math.max(
        catchupAudioRef.current.currentTime - seconds,
        0
      );
    }
  }, [isCatchupMode]);

  const seekToPosition = useCallback((timeInSeconds) => {
    if (!isCatchupMode || !catchupAudioRef.current) return;

    const audio = catchupAudioRef.current;
    const duration = Number.isFinite(audio.duration) ? audio.duration : catchupDuration;
    if (!duration || !Number.isFinite(duration)) return;

    const clamped = Math.max(0, Math.min(duration, timeInSeconds));
    audio.currentTime = clamped;
    setCatchupCurrentTime(clamped);
  }, [isCatchupMode, catchupDuration]);

  // Helper function to get display string for track info
  const getTrackDisplayString = useCallback(() => {
    const artist = trackInfo?.track_artist;
    const title = trackInfo?.track_title;
    
    if (artist && title) {
      return `${artist} - ${title}`;
    } else if (title) {
      return title;
    } else {
      return 'No track information';
    }
  }, [trackInfo]);

  const value = {
    stations,
    currentStation,
    isPlaying,
    volume,
    trackInfo,
    showArt,
    catchupShowArt,
    showTitle,

    isCatchupMode,
    catchupCurrentTime,
    catchupDuration,

    metadata,
    recentTracks,

    togglePlayback,
    setVolume,
    setCurrentStation,
    switchToLiveRadio,
    loadCatchupEpisode,
    playCatchup,
    pauseCatchup: () => {
      if (isCatchupMode && catchupAudioRef.current) {
        catchupAudioRef.current.pause();
      }
    },
    skipForward,
    skipBackward,
    seekToPosition,
    
    updateTrackInfo,  // RESTORED: Manually update track info for live radio transitions

    getTrackInfo: () => trackInfo,  // Returns the full data object
    getShowArt: () => showArt,
    getShowTitle: () => showTitle,
    getTrackDisplayString, // Returns a display-ready string

    fetchMetadataForStation: (stationId) => {
      const station = stations.find(s => s.id === stationId);
      if (station) {
        return loadMetadataForStation(station);
      }
      return null;
    },
  };

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
};